import { RefObject, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../../atoms/Button';

type Tool = 'pen' | 'highlighter' | 'eraser';

interface Stroke {
  tool: Tool;
  color: string;
  width: number;
  points: { x: number; y: number }[];
}

const COLORS = ['#1d4ed8', '#dc2626', '#fde047', '#000000'];

const STROKE_WIDTH: Record<Tool, number> = {
  pen: 3,
  highlighter: 18,
  eraser: 24,
};

interface ExerciseScribbleProps {
  containerRef: RefObject<HTMLElement>;
}

export const ExerciseScribble = ({ containerRef }: ExerciseScribbleProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tool, setTool] = useState<Tool>('pen');
  const [color, setColor] = useState<string>(COLORS[0]);
  const [strokes, setStrokes] = useState<Stroke[]>([]);
  const [container, setContainer] = useState<HTMLElement | null>(null);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawingRef = useRef(false);
  const currentStrokeRef = useRef<Stroke | null>(null);

  const drawStroke = (
    ctx: CanvasRenderingContext2D,
    stroke: Stroke
  ) => {
    if (stroke.points.length === 0) return;
    ctx.save();
    if (stroke.tool === 'eraser') {
      ctx.globalCompositeOperation = 'destination-out';
    } else if (stroke.tool === 'highlighter') {
      ctx.globalAlpha = 0.35;
    }
    ctx.strokeStyle = stroke.color;
    ctx.lineWidth = stroke.width;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(stroke.points[0].x, stroke.points[0].y);
    for (let i = 1; i < stroke.points.length; i++) {
      ctx.lineTo(stroke.points[i].x, stroke.points[i].y);
    }
    ctx.stroke();
    ctx.restore();
  };

  const redraw = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    for (const s of strokes) drawStroke(ctx, s);
    if (currentStrokeRef.current) drawStroke(ctx, currentStrokeRef.current);
  };

  useEffect(() => {
    if (!isOpen) {
      setContainer(null);
      return;
    }
    setContainer(containerRef.current);
  }, [isOpen, containerRef]);

  useEffect(() => {
    if (!isOpen || !container) return;

    const updateSize = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const { offsetWidth, offsetHeight } = container;
      if (canvas.width === offsetWidth && canvas.height === offsetHeight) {
        return;
      }
      canvas.width = offsetWidth;
      canvas.height = offsetHeight;
      redraw();
    };

    updateSize();
    const ro = new ResizeObserver(updateSize);
    ro.observe(container);
    return () => ro.disconnect();
  }, [isOpen, container]);

  useEffect(() => {
    redraw();
  }, [strokes]);

  const getPoint = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    isDrawingRef.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
    currentStrokeRef.current = {
      tool,
      color,
      width: STROKE_WIDTH[tool],
      points: [getPoint(e)],
    };
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isDrawingRef.current || !currentStrokeRef.current) return;
    currentStrokeRef.current.points.push(getPoint(e));
    redraw();
  };

  const handlePointerUp = () => {
    if (currentStrokeRef.current && currentStrokeRef.current.points.length > 0) {
      const finalized = currentStrokeRef.current;
      setStrokes((prev) => [...prev, finalized]);
    }
    currentStrokeRef.current = null;
    isDrawingRef.current = false;
  };

  const handleClear = () => setStrokes([]);

  const handleExit = () => {
    setStrokes([]);
    setIsOpen(false);
  };

  const toolButtonClass = (active: boolean) =>
    `flex h-10 w-10 items-center justify-center rounded-md transition ${
      active
        ? 'bg-brand-color-primary-pure text-mono-color-white'
        : 'bg-mono-color-white text-mono-color-darkest hover:bg-mono-color-lightest'
    }`;

  return (
    <>
      <Button variant='outlined' LeftIcon='draw' onClick={() => setIsOpen(true)}>
        Rabiscar
      </Button>
      {isOpen &&
        createPortal(
          <div
            className='
              fixed top-2 z-[60]
              flex flex-wrap items-center justify-center
              gap-xs xs:gap-sm
              rounded-md bg-mono-color-white p-2 shadow-level-2
              xs:left-2 xs:right-2
              md:left-1/2 md:right-auto md:top-16 md:-translate-x-1/2 md:flex-nowrap
            '
          >
            <button
              aria-label='Caneta'
              title='Caneta'
              className={toolButtonClass(tool === 'pen')}
              onClick={() => setTool('pen')}
            >
              <span className='material-symbols-outlined'>edit</span>
            </button>
            <button
              aria-label='Marca-texto'
              title='Marca-texto'
              className={toolButtonClass(tool === 'highlighter')}
              onClick={() => setTool('highlighter')}
            >
              <span className='material-symbols-outlined'>border_color</span>
            </button>
            <button
              aria-label='Borracha'
              title='Borracha'
              className={toolButtonClass(tool === 'eraser')}
              onClick={() => setTool('eraser')}
            >
              <span className='material-symbols-outlined'>ink_eraser</span>
            </button>
            <div className='mx-1 hidden h-6 w-px bg-mono-color-light md:block' />
            {COLORS.map((c) => (
              <button
                key={c}
                aria-label={`Cor ${c}`}
                onClick={() => setColor(c)}
                className={`h-7 w-7 rounded-full border-2 transition ${
                  color === c
                    ? 'border-brand-color-primary-pure'
                    : 'border-transparent'
                }`}
                style={{ backgroundColor: c }}
              />
            ))}
            <div className='mx-1 hidden h-6 w-px bg-mono-color-light md:block' />
            <Button
              variant='text'
              LeftIcon='delete'
              onlyIcon
              title='Limpar'
              onClick={handleClear}
              className='md:!hidden'
            />
            <Button
              variant='text'
              LeftIcon='close'
              onlyIcon
              title='Sair'
              onClick={handleExit}
              className='md:!hidden'
            />
            <Button
              variant='text'
              LeftIcon='delete'
              onClick={handleClear}
              className='!hidden md:!flex'
            >
              Limpar
            </Button>
            <Button
              variant='text'
              LeftIcon='close'
              onClick={handleExit}
              className='!hidden md:!flex'
            >
              Sair
            </Button>
          </div>,
          document.body
        )}
      {isOpen &&
        container &&
        createPortal(
          <canvas
            ref={canvasRef}
            className='absolute inset-0 z-[5] h-full w-full'
            style={{ touchAction: 'none', cursor: 'crosshair' }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
          />,
          container
        )}
    </>
  );
};
